// Level Data Schema
// Defines the structure for level JSON files

import { Vec2 } from '../utils/types';

// ============================================
// TERRAIN
// ============================================

export interface TerrainPath {
  // Array of points defining a terrain line [x, y]
  points: [number, number][];
  // Optional: is this a closed polygon (like an obstacle)?
  closed?: boolean;
  // Optional: terrain type for different visuals
  type?: 'ground' | 'ceiling' | 'obstacle' | 'platform';
  // Optional: visual properties
  color?: string;
}

// ============================================
// ENEMIES
// ============================================

export type EnemyType =
  | 'sentryNode'      // Basic turret, 2 hits, 100pts
  | 'pulseTower'      // Advanced turret, predictive, 4 hits, 250pts
  | 'shieldGenerator' // Protects nearby enemies, 3 hits, 400pts
  | 'conversionNode'  // Mission target, 10 hits, 1000pts
  | 'seekerDrone'     // Homing enemy, 1 hit, 50pts
  | 'resonanceSphere'; // Spawns drones, 5 hits, 500pts

export interface EnemySpawn {
  type: EnemyType;
  x: number;
  y: number;
  // When does this enemy activate?
  trigger: 'scroll' | 'proximity' | 'immediate' | 'onDestroy';
  // For 'proximity' trigger - how close player must be
  triggerRadius?: number;
  // For 'onDestroy' trigger - ID of enemy that must die first
  triggerTargetId?: string;
  // Optional unique ID for trigger references
  id?: string;
  // Optional: visual properties
  color?: string;
  shape?: [number, number][]; // Vertices relative to x,y position
  // Optional: custom properties override defaults
  properties?: {
    hp?: number;
    fireRate?: number;
    patrolPath?: [number, number][];
  };
}

// ============================================
// POWERUPS
// ============================================

export type PowerupType =
  | 'fuelPod'        // +25% fuel
  | 'fuelTank'       // +50% fuel
  | 'shieldCrystal'  // +15% shield
  | 'shieldCore'     // +35% shield
  | 'smartBomb'      // +1 bomb
  | 'pointStar'      // +500 points
  | 'extraLife'      // +1 life (rare)
  | 'weaponBoost';   // Faster firing for 30s

export interface PowerupPlacement {
  type: PowerupType;
  x: number;
  y: number;
  // Optional: visual properties
  color?: string;
  shape?: [number, number][]; // Vertices relative to x,y position
  // Optional: only appears after destroying specific enemy
  appearsOnDestroy?: string;
  // Optional: respawns after collection
  respawn?: boolean;
  respawnDelay?: number;
}

// ============================================
// HAZARDS & EFFECTS
// ============================================

export type HazardType =
  | 'gravityWell'     // Pulls player toward center
  | 'energyBarrier'   // Blocks movement, may cycle on/off
  | 'damageZone';     // Damages player while inside

export interface HazardPlacement {
  type: HazardType;
  x: number;
  y: number;
  width: number;
  height: number;
  // For energy barriers
  cycleOn?: number;   // Seconds on
  cycleOff?: number;  // Seconds off
  // For gravity wells
  strength?: number;
  // For damage zones
  damagePerSecond?: number;
}

// ============================================
// TRIGGERS & EVENTS
// ============================================

export type TriggerAction =
  | { type: 'spawnEnemy'; enemyType: EnemyType; x: number; y: number }
  | { type: 'showMessage'; text: string; duration: number }
  | { type: 'playSound'; sound: string }
  | { type: 'setScrollSpeed'; speed: number }
  | { type: 'activateHazard'; hazardId: string }
  | { type: 'checkpoint' };

export interface TriggerZone {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  // When triggered
  condition: 'playerEnter' | 'enemiesCleared' | 'timeElapsed';
  conditionValue?: number; // For timeElapsed
  // What happens
  actions: TriggerAction[];
  // Can only trigger once?
  oneShot?: boolean;
}

// ============================================
// OBJECTIVES
// ============================================

export interface LevelObjective {
  type: 'destroy' | 'collect' | 'reach' | 'survive' | 'protect';
  // For 'destroy': enemy type or ID to destroy
  target?: string;
  // For 'destroy'/'collect': how many
  count?: number;
  // For 'reach': position to reach
  position?: Vec2;
  // For 'survive': time in seconds
  duration?: number;
  // Description shown in HUD
  description: string;
  // Is this required to complete the level?
  required: boolean;
  // Bonus points for completion
  bonusPoints?: number;
}

// ============================================
// MAIN LEVEL DATA
// ============================================

export interface LevelData {
  // Metadata
  id: string;
  name: string;
  zone: number;        // 1-4
  levelNumber: number; // 1-4 within zone
  description?: string;
  parTime?: number;    // Seconds for par time bonus

  // World dimensions
  width: number;       // Total level width in pixels
  height: number;      // Level height (usually matches screen)

  // Scrolling
  scrollDirection: 'horizontal' | 'vertical' | 'both' | 'none';
  scrollSpeed: number; // Pixels per second (0 for player-controlled)
  autoScroll: boolean; // True = constant scroll, false = player-driven

  // Visual theme
  terrainColor?: string;
  backgroundColor?: string;

  // Gravity (0 for no gravity)
  gravity: number;

  // Player start
  spawnPoint: Vec2;

  // Level content
  terrain: TerrainPath[];
  enemies: EnemySpawn[];
  powerups: PowerupPlacement[];
  hazards?: HazardPlacement[];
  triggers?: TriggerZone[];

  // Objectives
  objectives: {
    primary: LevelObjective;
    secondary?: LevelObjective;
  };

  // Level end
  exitZone?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Validate level data structure
export function validateLevelData(data: unknown): data is LevelData {
  if (typeof data !== 'object' || data === null) return false;

  const level = data as Record<string, unknown>;

  // Check required fields
  const requiredFields = [
    'id', 'name', 'zone', 'levelNumber',
    'width', 'height', 'scrollDirection', 'scrollSpeed',
    'spawnPoint', 'terrain', 'enemies', 'powerups', 'objectives'
  ];

  for (const field of requiredFields) {
    if (!(field in level)) {
      console.error(`Level validation failed: missing field '${field}'`);
      return false;
    }
  }

  return true;
}

// Convert terrain points array to Vec2 array
export function terrainPointsToVec2(points: [number, number][]): Vec2[] {
  return points.map(([x, y]) => ({ x, y }));
}
